// ContactPanel.js
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button,
  Avatar,
  Badge,
  Tab,
  Tabs,
  CircularProgress
} from '@mui/material';
import {
  CloseRounded,
  Search,
  Add,
  FilterList,
  ArrowBack,
  Link as LinkIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import ChatLinkGenerator from './ChatLinkGenerator';
import { stringToColor, getInitials, truncateText } from './MessengerUtils';
import { useMessenger } from './MessengerContext';

const ContactPanel = ({ toggleMessenger, preloadedContacts = [], preloadedMessages = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [showNewChatSection, setShowNewChatSection] = useState(false);
  const [newChatTab, setNewChatTab] = useState(0); // 0: Contacts, 1: Invite Link
  const { allChats, openChat, createChat } = useMessenger();
  const [contacts, setContacts] = useState([]);
  const [existingChats, setExistingChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use preloaded contacts and messages instead of making API calls
  useEffect(() => {
    if (preloadedContacts && preloadedContacts.length > 0) {
      console.log('Using preloaded contacts in ContactPanel:', preloadedContacts.length);
      setContacts(preloadedContacts);
      
      // Create existing chats list from contacts with chat_uuid and include preloaded messages
      const chats = preloadedContacts
        .filter(client => client.chat_uuid)
        .map(client => {
          // Get messages for this chat if available
          const chatMessages = preloadedMessages[client.chat_uuid] || [];
          console.log(`Chat ${client.chat_uuid} has ${chatMessages.length} preloaded messages`);
          
          return {
            id: client.chat_uuid,
            chat_uuid: client.chat_uuid,
            participants: [client],
            messages: chatMessages,
            unreadCount: 0, // Could calculate from messages if needed
            name: client.name,
            isGroup: false
          };
        });
      
      setExistingChats(chats);
      setIsLoading(false);
    } else {
      console.log('No preloaded contacts available');
      setContacts([]);
      setExistingChats([]);
      setIsLoading(false);
    }
  }, [preloadedContacts, preloadedMessages]);
  
  // Filter users based on search term
  const filteredUsers = contacts.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter users without an existing chat (for New Chat section)
  const usersWithoutChat = contacts.filter(user => !user.chat_uuid);
  const filteredUsersWithoutChat = usersWithoutChat.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Combine existing chats with allChats list
  const combinedChats = [...(allChats || [])];

  // Add existing chats that aren't already in allChats
  existingChats.forEach(existingChat => {
    const existingIndex = combinedChats.findIndex(chat => 
      chat.id === existingChat.id || chat.chat_uuid === existingChat.chat_uuid
    );
    
    if (existingIndex >= 0) {
      // Chat exists but may not have messages - merge them
      if (!combinedChats[existingIndex].messages || combinedChats[existingIndex].messages.length === 0) {
        combinedChats[existingIndex].messages = existingChat.messages || [];
      }
    } else {
      // Add new chat
      combinedChats.push(existingChat);
    }
  });
  
  // Filter out demo accounts from combined chats in production
  const demoNames = ['James Wilson', 'Sarah Johnson'];
  const filteredCombinedChats = process.env.NODE_ENV === 'production'
    ? combinedChats.filter(chat => {
        // Filter out chats with demo participants
        if (!chat.participants || chat.participants.length === 0) return true;
        return !chat.participants.some(p => demoNames.includes(p.name));
      })
    : combinedChats;
  
  // Filter chats based on search term
  const filteredChats = filteredCombinedChats.filter(chat => {
    const participantName = chat.participants && chat.participants[0] ? chat.participants[0].name : '';
    return participantName.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  const handleCreateChat = (userId) => {
    const user = contacts.find(u => u.id === userId);
    if (user) {
      if (user.chat_uuid) {
        // If user already has a chat, open it instead of creating a new one
        openChat(user.chat_uuid);
      } else {
        // Otherwise create a new chat
        createChat(user);
      }
      setShowNewChatSection(false);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  return (
    <Paper
      elevation={6}
      sx={{ 
        position: 'fixed', 
        bottom: 96, 
        right: 24, 
        width: 320, 
        maxHeight: '70vh', 
        zIndex: 1400, 
        borderRadius: 2, 
        display: 'flex', 
        flexDirection: 'column'
      }}
    >
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        {showNewChatSection ? (
          <>
            <IconButton 
              onClick={() => setShowNewChatSection(false)} 
              sx={{ color: 'white', mr: 1 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6">New Message</Typography>
          </>
        ) : (
          <Typography variant="h6">Messenger</Typography>
        )}
        <IconButton onClick={toggleMessenger} sx={{ color: 'white' }}>
          <CloseRounded />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error">{error}</Typography>
          <Button 
            variant="outlined" 
            sx={{ mt: 2 }}
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </Box>
      ) : showNewChatSection ? (
        <Box sx={{ overflow: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={newChatTab} 
              onChange={(e, newValue) => setNewChatTab(newValue)} 
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab icon={<PersonIcon />} label="Contacts" />
              <Tab icon={<LinkIcon />} label="Invite Link" />
            </Tabs>
          </Box>
          
          {/* Contacts Tab */}
          <Box sx={{ 
            display: newChatTab === 0 ? 'flex' : 'none', 
            flexDirection: 'column',
            flexGrow: 1,
            overflow: 'auto'
          }}>
            <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}>
              Select a contact to start a conversation
            </Typography>
            
            <List dense>
              {filteredUsersWithoutChat.length > 0 ? (
                filteredUsersWithoutChat.map((user) => (
                  <ListItem key={user.id} button onClick={() => handleCreateChat(user.id)}>
                    <ListItemAvatar>
                      <Avatar
                        src={user.avatar}
                        sx={{
                          bgcolor: stringToColor(user.name),
                          width: 40,
                          height: 40,
                          position: 'relative'
                        }}
                      >
                        {getInitials(user.name)}
                        {user.isOnline && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: -2,
                              right: -2,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: 'success.main',
                              border: '2px solid white'
                            }}
                          />
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={user.name} 
                      secondary={user.role === 'therapist' ? 'Therapist' : 'Client'}
                      primaryTypographyProps={{ fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    {user.isOnline && (
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium' }}>
                        Online
                      </Typography>
                    )}
                  </ListItem>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm 
                      ? 'No matching users found without existing chats' 
                      : 'No users available for new chats'}
                  </Typography>
                </Box>
              )}
            </List>
          </Box>
          
          {/* Invite Link Tab */}
          <Box sx={{ 
            display: newChatTab === 1 ? 'block' : 'none', 
            p: 2,
            flexGrow: 1,
            overflow: 'auto'
          }}>
            <ChatLinkGenerator 
              onClose={() => setShowNewChatSection(false)}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange} 
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Chats" />
              <Tab label="Contacts" />
            </Tabs>
          </Box>
          
          <Box sx={{ overflow: 'auto', flexGrow: 1, display: currentTab === 0 ? 'block' : 'none' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">Recent Chats</Typography>
              <Button 
                startIcon={<Add />} 
                size="small" 
                onClick={() => setShowNewChatSection(true)}
                disabled={usersWithoutChat.length === 0}
              >
                New Chat
              </Button>
            </Box>
            
            <List dense>
              {filteredChats.length > 0 ? (
                filteredChats.map((chat) => {
                  if (!chat.participants || !chat.participants[0]) return null;
                  const contact = chat.participants[0];
                  const lastMessage = chat.messages && chat.messages.length > 0 
                    ? chat.messages[chat.messages.length - 1] 
                    : null;
                  
                  return (
                    <ListItem 
                      button 
                      key={chat.id || chat.chat_uuid}
                      onClick={() => openChat(chat.id || chat.chat_uuid)}
                      alignItems="flex-start"
                      sx={{ 
                        bgcolor: chat.unreadCount > 0 ? 'action.selected' : 'inherit',
                        '&:hover': {
                          bgcolor: chat.unreadCount > 0 ? 'action.selected' : 'action.hover',
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          color="primary"
                          variant="dot"
                          invisible={!contact.isOnline}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          overlap="circular"
                        >
                          <Avatar
                            src={contact.avatar}
                            sx={{
                              bgcolor: stringToColor(contact.name),
                              width: 48,
                              height: 48
                            }}
                          >
                            {getInitials(contact.name)}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={contact.name}
                        secondary={
                          lastMessage 
                            ? truncateText(`${lastMessage.senderId === 'current-user' ? 'You: ' : ''}${lastMessage.content}`, 40)
                            : 'No messages yet'
                        }
                        primaryTypographyProps={{ 
                          fontWeight: chat.unreadCount > 0 ? 'bold' : 'medium',
                          noWrap: true 
                        }}
                        secondaryTypographyProps={{ 
                          variant: 'body2', 
                          noWrap: true,
                          color: chat.unreadCount > 0 ? 'text.primary' : 'text.secondary',
                          fontWeight: chat.unreadCount > 0 ? 'medium' : 'regular'
                        }}
                        sx={{ my: 0 }}
                      />
                      {chat.unreadCount > 0 && (
                        <Badge 
                          color="primary" 
                          badgeContent={chat.unreadCount} 
                          sx={{ alignSelf: 'center', ml: 1 }}
                        />
                      )}
                    </ListItem>
                  );
                })
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary">No recent chats</Typography>
                  {usersWithoutChat.length > 0 && (
                    <Button 
                      variant="outlined" 
                      startIcon={<Add />}
                      sx={{ mt: 1 }}
                      onClick={() => setShowNewChatSection(true)}
                    >
                      Start a new chat
                    </Button>
                  )}
                </Box>
              )}
            </List>
          </Box>
          
          <Box sx={{ overflow: 'auto', flexGrow: 1, display: currentTab === 1 ? 'block' : 'none' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">Online Contacts</Typography>
              <IconButton size="small">
                <FilterList fontSize="small" />
              </IconButton>
            </Box>
            
            <List dense>
              {filteredUsers.filter(u => u.isOnline).length > 0 ? (
                filteredUsers.filter(u => u.isOnline).map((user) => (
                  <ListItem 
                    key={user.id} 
                    button 
                    onClick={() => user.chat_uuid ? openChat(user.chat_uuid) : handleCreateChat(user.id)}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={user.avatar}
                        sx={{
                          bgcolor: stringToColor(user.name),
                          width: 40,
                          height: 40,
                          position: 'relative'
                        }}
                      >
                        {getInitials(user.name)}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            border: '2px solid white'
                          }}
                        />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={user.name} 
                      secondary={user.role === 'therapist' ? 'Therapist' : 'Client'}
                      primaryTypographyProps={{ fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    {user.chat_uuid && (
                      <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'medium', ml: 1 }}>
                        Existing Chat
                      </Typography>
                    )}
                  </ListItem>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">No contacts online</Typography>
                </Box>
              )}
            </List>
            
            <Divider sx={{ my: 1 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">All Contacts</Typography>
            </Box>
            
            <List dense>
              {filteredUsers.map((user) => (
                <ListItem 
                  key={user.id} 
                  button 
                  onClick={() => user.chat_uuid ? openChat(user.chat_uuid) : handleCreateChat(user.id)}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={user.avatar}
                      sx={{
                        bgcolor: stringToColor(user.name),
                        width: 40,
                        height: 40,
                        position: 'relative'
                      }}
                    >
                      {getInitials(user.name)}
                      {user.isOnline && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            border: '2px solid white'
                          }}
                        />
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={user.name} 
                    secondary={user.role === 'therapist' ? 'Therapist' : 'Client'}
                    primaryTypographyProps={{ fontWeight: 'medium' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {user.isOnline && (
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 'medium' }}>
                        Online
                      </Typography>
                    )}
                    {user.chat_uuid && (
                      <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'medium' }}>
                        Existing Chat
                      </Typography>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ContactPanel;